/**
 * Block 12 PR-C Phase C7 Step 3 — FreeAnketaFormContainer.
 *
 * Container для свободного режима Other anketa. Per ADR-0020 §Q9 + §C7.1
 * Вариант 1: `AnketaFormContainer` (outer URL-guard) делегирует сюда
 * через type='other' branch — не отдельный router entry.
 *
 * **Two-layer split** (outer/inner):
 *   - Outer `FreeAnketaFormContainer` ловит draft hydration через `getProject`
 *     и renders «Загрузка...» пока async resolve. После resolve монтирует
 *     Inner с уже зарезолвленными `projectId` + `initialDraft`.
 *   - Inner `FreeAnketaFormContainerInner` вызывает `useFreeAnketaForm` с
 *     `initialDraft` уже определённым — useState initializer hydrates state
 *     корректно (один раз на mount, no further re-init на prop change).
 *
 * **Foreign-type guard:** если `?draftId=` указывает на record с
 * `params.type !== 'other'` (например, draft от kitchen anketa случайно
 * передан через URL share), outer **не** reuses этот draftId как projectId.
 * Иначе первое же изменение state перезапишет kitchen record как 'other'
 * → silent data loss. Fallback — fresh UUID + empty form.
 *
 * **Known limitation:** смена `?draftId=` query во время жизни компонента
 * не remount'ит Inner (initialDraft/projectId зафиксированы при первом
 * `draftLoaded=true`). В текущем UX нет path смены draftId без navigate,
 * поэтому не критично; если потребуется — добавить `key={projectId}` на Inner.
 *
 * Reference: ADR-0020 §Q9 + §Q11, ADR-0019 §Q11.2-§Q11.4.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getProject } from '@/lib/offline/idb/projects';
import { genUuid } from '@/lib/uuid';
import { useAuthStore } from '@/lib/stores/authStore';
import { FreeAnketaFormView } from './FreeAnketaFormView';
import { useFreeAnketaForm } from './useFreeAnketaForm';
import { useFreeAnketaPersist } from './useFreeAnketaPersist';
import type { FreeAnketaState, FreeRow } from './types';

export function FreeAnketaFormContainer() {
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draftId');

  const [draftLoaded, setDraftLoaded] = useState(!draftId);
  const [initialDraft, setInitialDraft] = useState<FreeAnketaState | undefined>(undefined);
  // resolvedProjectId — final UUID для Inner. При foreign-type draftId
  // (или non-existing) генерируется fresh UUID чтобы не перезаписать чужой record.
  const [resolvedProjectId, setResolvedProjectId] = useState<string | null>(
    draftId ? null : genUuid(),
  );

  useEffect(() => {
    if (!draftId) {
      setDraftLoaded(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const record = await getProject(draftId);
        if (cancelled) return;
        if (record && record.data && record.data.params?.type === 'other') {
          // Safe to reuse draftId — record is genuine 'other'.
          const freeRows: FreeRow[] = (record.data.freeItems ?? []).map((item, idx) => ({
            id: `${draftId}-row-${idx}`,
            name: item.name,
            quantity: item.quantity,
            unit: item.unit,
            price: item.price,
          }));
          setInitialDraft({
            projectId: draftId,
            projectName: record.name,
            freeRows,
            isDirty: false,
            createdAt: record.createdAt,
            updatedAt: record.updatedAt,
          });
          setResolvedProjectId(draftId);
        } else {
          // Record не существует ИЛИ foreign type → fresh UUID, empty form.
          // Защищает чужие records от silent overwrite через debounced saveProject.
          if (record && import.meta.env.DEV) {
            console.warn(
              `[FreeAnketaFormContainer] draftId=${draftId} указывает на не-Other record (type=${record.data?.params?.type}); запускаем пустую анкету с новым UUID.`,
            );
          }
          setResolvedProjectId(genUuid());
        }
        setDraftLoaded(true);
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[FreeAnketaFormContainer] draft hydration failed', err);
        }
        setResolvedProjectId(genUuid());
        setDraftLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draftId]);

  if (!draftLoaded || resolvedProjectId === null) {
    return (
      <p className="text-muted text-center p-8" role="status">
        Загрузка проекта...
      </p>
    );
  }

  return <FreeAnketaFormContainerInner projectId={resolvedProjectId} initialDraft={initialDraft} />;
}

interface InnerProps {
  projectId: string;
  initialDraft: FreeAnketaState | undefined;
}

function FreeAnketaFormContainerInner({ projectId, initialDraft }: InnerProps) {
  const navigate = useNavigate();
  const { state, actions, total, isValid } = useFreeAnketaForm(projectId, initialDraft);
  const userId = useAuthStore((s) => s.profile?.phone) ?? 'anon-user';
  const { forcePersist, persistStatus } = useFreeAnketaPersist(state, userId);

  const onSave = useCallback(async () => {
    if (!isValid) return;
    await forcePersist();
    // Phase 9+: toast / navigate to project list. Сейчас остаёмся на экране.
  }, [isValid, forcePersist]);

  // КП CTA — mirrors AnketaFormContainer onCreateQuote pattern (Phase B5 Step 2).
  // encodeURIComponent defensive: projectId обычно genUuid() (safe),
  // но из ?draftId= URL может прийти non-canonical → encode защищает.
  // B-098 parallel: forcePersist rejection silently swallowed; navigate
  // выполнится с stale snapshot (если IDB quota/SecurityError) — unified
  // error handling запланирован Phase D / PR-D.
  const onCreateQuote = useCallback(async () => {
    await forcePersist();
    navigate(`/calculator/quote?projectId=${encodeURIComponent(projectId)}`);
  }, [forcePersist, navigate, projectId]);

  return (
    <FreeAnketaFormView
      state={state}
      total={total}
      isValid={isValid}
      actions={actions}
      isSaving={persistStatus === 'saving'}
      onSave={onSave}
      onCreateQuote={onCreateQuote}
    />
  );
}
